import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './auth-context';
import { authFetch } from '../utils/auth-fetch';
import { PlayerAutocomplete } from '../leagues/player-autocomplete';

const CLAIM_ERRORS: Record<string, string> = {
  PLAYER_TAKEN: 'Tego zawodnika śledzi już inne konto. Jeśli to Ty, odezwij się do organizatora.',
  PLAYER_NOT_FOUND: 'Nie znaleziono takiego zawodnika.',
};

function claimError(code: string): string {
  return CLAIM_ERRORS[code] ?? 'Nie udało się zapisać wyboru.';
}

function monogram(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }

  return label.trim().charAt(0).toUpperCase() || '?';
}

export const MePage: React.FC = () => {
  const { user, loading, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  async function claimPlayer(playerId: number): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      const res = await authFetch('/api/me/player', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(claimError(body.error ?? ''));

        return;
      }

      setPicking(false);
      await refresh();
    } catch {
      setError('Błąd sieci.');
    } finally {
      setBusy(false);
    }
  }

  async function releasePlayer(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      await authFetch('/api/me/player', { method: 'DELETE' });
      await refresh();
    } catch {
      setError('Błąd sieci.');
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout(): Promise<void> {
    await logout();
    navigate('/');
  }

  if (loading) {
    return <div className="event-detail-loading">Ładowanie…</div>;
  }

  if (!user) {
    return (
      <div className="auth-page">
        <div className="account-card account-card--empty">
          <p className="account-card__eyebrow">Twoje konto</p>
          <h1 className="account-card__name">Nie jesteś zalogowany</h1>
          <p className="account-card__lead">
            Ta strona pokazuje, z którym zawodnikiem powiązane jest Twoje konto.
          </p>
          <Link to="/login" className="auth-submit account-card__cta">Zaloguj się</Link>
        </div>
      </div>
    );
  }

  const displayName = user.name ?? user.email;
  const linkedPlayerId = user.playerId;
  const showPicker = !linkedPlayerId || picking;

  return (
    <div className="auth-page">
      <div className="account-card" role="region" aria-label="Twoje konto">
        <header className="account-card__head">
          <span className="account-card__avatar" aria-hidden="true">{monogram(displayName)}</span>
          <div className="account-card__identity">
            <p className="account-card__eyebrow">Twoje konto</p>
            <h1 className="account-card__name">{displayName}</h1>
            {user.name && <p className="account-card__email">{user.email}</p>}
          </div>
        </header>

        <section className="account-player" data-testid="account-player-section">
          <div className="account-player__head">
            <h2 className="account-player__title">Twój zawodnik</h2>
            <span
              className={`account-player__status${linkedPlayerId ? ' is-linked' : ''}`}
              data-testid="account-player-status"
            >
              {linkedPlayerId ? 'Powiązany' : 'Brak powiązania'}
            </span>
          </div>

          {showPicker && (
            <p className="account-player__hint">
              {linkedPlayerId
                ? 'Wybierz innego zawodnika — dotychczasowe powiązanie zostanie zastąpione.'
                : 'Wskaż siebie w bazie zawodników — bez tego nie zapiszesz się na kolejkę ani nie zgłosisz się w poszukiwaniu partnera.'}
            </p>
          )}

          {error && <p className="auth-error" data-testid="account-player-error">{error}</p>}

          {showPicker ? (
            <div className="account-player__picker" data-testid="account-player-picker">
              <PlayerAutocomplete
                placeholder="Wpisz swoje imię i nazwisko…"
                ratingKind="singles"
                allowCreate={false}
                disabled={busy}
                onSelect={(picked) => {
                  if ('newPlayer' in picked) {
                    return;
                  }

                  claimPlayer(picked.id);
                }}
              />
              {picking && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  data-testid="account-player-cancel"
                  onClick={() => { setPicking(false); setError(null); }}
                >
                  Anuluj
                </button>
              )}
            </div>
          ) : (
            <div className="account-player__current" data-testid="account-player-current">
              <Link to={`/player/${linkedPlayerId}`} className="account-player__name">
                {user.name ?? `Zawodnik #${linkedPlayerId}`}
              </Link>
              <span className="account-player__actions">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  data-testid="account-player-change"
                  onClick={() => setPicking(true)}
                >
                  Zmień
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  data-testid="account-player-release"
                  onClick={releasePlayer}
                >
                  Odepnij
                </button>
              </span>
            </div>
          )}
        </section>

        <footer className="account-card__foot">
          <button type="button" onClick={handleLogout} className="account-card__logout">
            Wyloguj
          </button>
        </footer>
      </div>
    </div>
  );
};
